import type { ReactElement } from 'react';
import { guards } from '@shared/helpers/typeguards.helpers';

/** A report page together with the visibility state its thumbnail renders. */
export type ReportPageItem = {
  /** Stable identity of the page — its React `key`, or a positional fallback. */
  key: string;
  page: ReactElement;
  /** Hidden pages stay in the thumbnail list but leave the canvas, fullscreen and PDF. */
  isHidden: boolean;
  /** Index among the visible pages, `null` when hidden. */
  visibleIndex: number | null;
  /**
   * Visible page to scroll to for this thumbnail. For a visible page that is
   * itself; for a hidden one it is the slot the page would occupy once shown,
   * so clicking a skipped thumbnail lands where it belongs. Equals the visible
   * page count when the hidden page sorts last — no such page exists, and the
   * caller scrolls to the end instead.
   */
  scrollIndex: number;
};

/**
 * Identity of a report page. Callers key their pages, so a missing key only
 * happens for pages that cannot be toggled anyway — fall back to the position.
 */
export const getReportPageKey = (page: ReactElement, index: number): string =>
  guards.is.string(page.key) ? page.key : `report-page-${index}`;

/**
 * Pair each page with its hidden state and its position among the visible pages.
 */
export const toReportPageItems = (
  pages: readonly ReactElement[],
  hiddenPageKeys: readonly string[] = [],
): ReportPageItem[] => {
  let visibleCount = 0;
  return pages.map((page, index) => {
    const key = getReportPageKey(page, index);
    const isHidden = hiddenPageKeys.includes(key);
    const scrollIndex = visibleCount;
    return {
      key,
      page,
      isHidden,
      visibleIndex: isHidden ? null : visibleCount++,
      scrollIndex,
    };
  });
};

/** Pages rendered on the canvas, in fullscreen and in the PDF export. */
export const getVisibleReportPages = (
  items: readonly ReportPageItem[],
): ReactElement[] =>
  items.filter((item) => !item.isHidden).map((item) => item.page);

/** Stable keys of canvas pages, in canvas order. */
export const getVisibleReportPageKeys = (
  items: readonly ReportPageItem[],
): string[] =>
  items.filter((item) => item.visibleIndex !== null).map((item) => item.key);

/**
 * Pages listed in the thumbnail panel.
 * Hidden pages stay listed only when the viewer can unskip them (the eye toggle).
 * Shared and other read-only reports omit skipped slides entirely.
 */
export const getReportPanelPageItems = (
  items: readonly ReportPageItem[],
  canToggleHidden: boolean,
): ReportPageItem[] =>
  canToggleHidden ? [...items] : items.filter((item) => !item.isHidden);
